/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { RatingGaugeItem } from './RatingGaugeItem';
import { CommitDetail, InputDetail, useEvents } from './useEvents';
import { classNames } from '../utils/UNSAFE_classNames';
import { getDimensions } from './RatingUtils';
import { useTooltipOrDatatip } from './useTooltipOrDatatip';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
/**
 * Props for the RatingGauge
 */
type Props = {
  /**
   * Defines whether the rating gauge should be read only. User interaction is prevented if set to true.
   */
  isReadonly?: boolean;

  /**
   * Defines whether the gauge is disabled or not. User interaction is prevented and the rating gauge is hidden from screen readers if set to true.
   */
  isDisabled?: boolean;

  /**
   * The size of the individual rating gauge items.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * The color of the individual rating gauge items.
   */
  color?: 'neutral' | 'gold';

  /**
   * The value of the rating gauge.
   */
  value?: number;

  /**
   * Specifies the increment by which values can be changed.
   */
  step?: number;

  /**
   * Integer value specifying the maximum value of the gauge, which determines the number of items that are displayed
   */
  max?: number;

  /**
   * The callback called in response to hover or focus interactions with the rating gauge items.
   */
  onInput?: (detail: InputDetail) => void;

  /**
   * The callback called when value of gauge changes on blur or Enter or click.
   */
  onCommit?: (detail: CommitDetail) => void;

  /**
   * The rating gauge tooltip. Tooltip is only used for read only rating gauges. If accessibleLabel is not specified, tooltip will be used as the accessible label.
   */
  tooltip?: string;

  /**
   * The rating gauge datatip string. Datatip is used for interactive rating gauges.
   */
  datatip?: string;

  /**
   * A label to be used for accessibility purposes. Default label will be used if not provided.
   */
  accessibleLabel?: string;

  /**
   * One or more ids (separated by spaces) of elements that label the rating gauge.
   */
  ariaLabelledBy?: string;

  /**
   * One or more ids (separated by spaces) of elements that describe the rating gauge.
   */
  ariaDescribedBy?: string;
};

const getAriaLabel = (
  props: Pick<Props, 'accessibleLabel' | 'tooltip' | 'isReadonly' | 'isDisabled'>
) => {
  return props.accessibleLabel
    ? props.accessibleLabel
    : props.tooltip && props.isReadonly && !props.isDisabled
    ? props.tooltip
    : undefined;
};
/**
 * A rating gauge displays information graphically, typically displaying or accepting user feedback on a product or service.
 */
export function RatingGauge({
  max = 5,
  value = 0,
  size = 'md',
  color = 'neutral',
  step = 1,
  isReadonly,
  isDisabled,
  ...props
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef(getDimensions(null));

  const isInteractive = !isReadonly && !isDisabled;

  // Since the dimension are compute lazily after render, store width and x in ref
  // This is used to identify hovered and clicked star.
  // width of the gauge depends on size and max so passing them as dependencies for useEffect
  useEffect(() => {
    dimensionsRef.current = getDimensions(rootRef.current);
  }, [size, max]);

  const eventProps = useEvents(
    max,
    step,
    value,
    rootRef,
    dimensionsRef,
    isInteractive,
    props.onCommit,
    props.onInput
  );

  const { tooltipContent, tooltipProps } = useTooltipOrDatatip({
    max,
    value,
    isReadonly,
    isDisabled,
    tooltip: props.tooltip,
    datatip: props.datatip,
    width: dimensionsRef.current.width,
    ariaDescribedBy: props.ariaDescribedBy
  });

  const mergedEventProps = mergeProps(eventProps, tooltipProps);
  const ariaLabel = getAriaLabel({
    accessibleLabel: props.accessibleLabel,
    tooltip: props.tooltip,
    isReadonly,
    isDisabled
  });
  const { isTabbable } = useTabbableMode();
  return (
    <Fragment>
      <div
        ref={rootRef}
        class={classNames([
          ratingStyles.base,
          isInteractive ? ratingStyles.interactive : undefined
        ])}
        aria-label={ariaLabel}
        role="slider"
        aria-valuemin="0"
        aria-valuenow={value}
        aria-valuemax={max}
        aria-valuetext={value}
        aria-labelledby={props.ariaLabelledBy}
        tabIndex={isTabbable && !isDisabled ? 0 : -1}
        aria-disabled={isDisabled ? 'true' : undefined}
        aria-readonly={isReadonly && !isDisabled ? 'true' : undefined}
        {...mergedEventProps}>
        {[...Array(max)].map((_, index: number) => {
          const fillRatio = Math.min(Math.max(0, value - index), 1);
          return (
            <div className={classNames([ratingStyles[size], ratingStyles.item])}>
              <RatingGaugeItem
                fillRatio={fillRatio}
                isDisabled={isDisabled}
                isReadonly={isReadonly}
                color={color}
              />
            </div>
          );
        })}
      </div>
      {tooltipContent}
    </Fragment>
  );
}

const ratingStyles = {
  base: cssProps`${{
    display: 'inline-flex',
    '&:focus-visible': {
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
      outlineStyle: 'dotted',
      outlineWidth: '1px'
    }
  }}`,
  interactive: cssProps`${{
    cursor: 'pointer',
    touchAction: 'none'
  }}`,
  lg: cssProps`${{
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-lg-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-lg-size)',
    margin: '0px 2px'
  }}`,
  md: cssProps`${{
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-md-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-md-size)',
    margin: '0px 1px'
  }}`,
  sm: cssProps`${{
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-sm-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-rating-gauge-sm-size)',
    margin: '0px 1px'
  }}`,
  item: cssProps`${{
    position: 'relative'
  }}`
};
